import { useState } from "react";
import { CompanionWeapon, CompanionWeaponBlock, CompanionWeaponBlockBase } from "../../../model/character";
import { v4 } from 'uuid';

import { Box, Stack, styled, TextField, Typography } from "@mui/material";
import { NumberField } from "../../../components/formFields/NumberField";
import StatLookupField from "../../../components/StatLookupField";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";
import { ModalAction, ModalContentSection } from "../../../components/Modal";
import ButtonFlat, { ButtonFlatIcon } from "../../../components/ButtonFlat";

import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate, useParams } from "react-router-dom";
import { exportData, loadLibraryData, saveLibraryData } from "../../../master/libraryData";
import AbilityEditor from "../../characterPage/AbilityEditor";
import Page from "../../../components/Page";
import { CreateDefaultCombatSkills } from "../../../master/data";
import TagList from "../../../components/TagList";
import { StatDisplay, StrongHitRangeLabel } from "../../../components/StatDisplay";
import { defaultCompanionWeaponBase, defaultCompanionWeaponBlock } from "../../../master/defaults";

const CompanionWeaponBlockEditor = (props: {
        weaponBlock: CompanionWeaponBlock | CompanionWeaponBlockBase,
        setWeaponBlock: (block: CompanionWeaponBlock | CompanionWeaponBlockBase) => void,
        isBase?: boolean
    }
    ) => {
    const { weaponBlock: companionWeaponBlock, setWeaponBlock: setCompanionWeaponBlock } = props;

    const onChange = (key: string, value: any) => {
        setCompanionWeaponBlock({
            ...companionWeaponBlock,
            [key]: value
        })
    }

    const handleAddFeature = (item: string) => {
        const newFeatures = [ ...companionWeaponBlock.addFeatures, item ];
        onChange("addFeatures", newFeatures);

    }

    const handleRemoveFeature = (index: number) => {
        const features = [ ...companionWeaponBlock.addFeatures ];
        features.splice(index, 1);
        onChange("addFeatures", features);
    }

    const handleAddWeaponType = (item: string) => {
        const items = [ ...companionWeaponBlock.weaponType, item ];
        onChange("weaponType", items);

    }

    const handleRemoveWeaponType = (index: number) => {
        const items = [ ...companionWeaponBlock.weaponType ];
        items.splice(index, 1);
        onChange("weaponType", items);
    }

    return <>
        <Stack spacing={1}>
            <FormFieldLabel label="Name">
                <TextField fullWidth type="text" variant="standard" value={companionWeaponBlock.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Hit Dice" justifyContent="end">
                <NumberField step={1} min={0} value={companionWeaponBlock.hitDice} onChange={(value) => onChange("hitDice", value)} customRenderer={(val) => `${val}d6`}/>
            </FormFieldLabel>
            <FormFieldLabel label="Hit Bonus" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.hitBonus} onChange={(value) => onChange("hitBonus", value)} customRenderer={(v) => StatDisplay(v, "", true)} />
            </FormFieldLabel>
            {props.isBase &&
            <FormFieldLabel label="Skill" justifyContent="end"><StatLookupField onChange={(value) => onChange("skillBonusId", value)}
                //@ts-ignore
                value={companionWeaponBlock.skillBonusId}
                stats={[...CreateDefaultCombatSkills()]} />
                </FormFieldLabel> }
            <FormFieldLabel label="Range" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.range} onChange={(value) => onChange("range", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Endurance Damage" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.enduranceDamage} onChange={(value) => onChange("enduranceDamage", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Crit Damage" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.critDamage} onChange={(value) => onChange("critDamage", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Equipment Slots" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.slots} onChange={(value) => onChange("slots", value)} />
            </FormFieldLabel>
            <FormFieldLabel label={`Strong Hit Range (${StrongHitRangeLabel(companionWeaponBlock.strongHitRange)})`} justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.strongHitRange} onChange={(value) => onChange("strongHitRange", value)}
                    customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Reload" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.reload} onChange={(value) => onChange("reload", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Defence" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.defence} onChange={(value) => onChange("defence", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Armour" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.armour} onChange={(value) => onChange("armour", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v}/>
            </FormFieldLabel>
            <FormFieldLabel label="Durability" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.durability} onChange={(value) => onChange("durability", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Bodies" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.bodies} onChange={(value) => onChange("bodies", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Mobility" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.mobility} onChange={(value) => onChange("mobility", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v}/>
            </FormFieldLabel>
            <FormFieldLabel label="Acquire" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.acquire} onChange={(value) => onChange("acquire", value)} customRenderer={(v) => StatDisplay(v, "t", !props.isBase)}/>
            </FormFieldLabel>
            <FormFieldLabel label="Influence" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.influence} onChange={(value) => onChange("influence", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Resources" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.resources} onChange={(value) => onChange("resources", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Knowledge" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.knowledge} onChange={(value) => onChange("knowledge", value)} customRenderer={(v) => `${v}kn`} />
            </FormFieldLabel>
        </Stack>
        <ModalContentSection title="Weapon Type">
            <TagList items={companionWeaponBlock.weaponType} onAdd={handleAddWeaponType} onRemove={handleRemoveWeaponType} />
        </ModalContentSection>
        <ModalContentSection title="Keywords">
            <TagList items={companionWeaponBlock.addFeatures} onAdd={handleAddFeature} onRemove={handleRemoveFeature} />
        </ModalContentSection>
        <AbilityEditor title="Strong Hit Options" abilities={companionWeaponBlock.strongHits} setAbilities={(value) => onChange("strongHits", value)} />
        <AbilityEditor title="Passive Abilities" abilities={companionWeaponBlock.passives} setAbilities={(value) => onChange("passives", value)} />
        {props.isBase !== true && <Box paddingLeft={1}>
            <Typography>Notes</Typography>
            <TextField value={companionWeaponBlock.notes} onChange={(event) => onChange('notes', event.currentTarget.value)} multiline fullWidth />
        </Box>}
    </>
}

export const GetNewCompanion = (isBase: boolean, name: string): CompanionWeaponBlock => {
    return {
        ...(isBase ? defaultCompanionWeaponBase : defaultCompanionWeaponBlock),
        name: name,
        id: v4()
    }
}

export const CompanionWeaponPage = (props: { prefix: string, pathType: string, isbase?: boolean, defaultName: string}) => {
    const { prefix, pathType, defaultName } = props;
    const { id } = useParams();
    const navigate = useNavigate();
    const [ current, setCurrent ] = useState<CompanionWeaponBlock>(loadLibraryData<CompanionWeaponBlock>(props.prefix, id ?? "NONE") ?? GetNewCompanion(props.isbase === true, defaultName));

    const handleSave = () => {
        navigate(`/library/${pathType}/${current.id}`, {replace: true});
        saveLibraryData(prefix, current);
    }

    const handleBack = () => {
        navigate("/library");
    }

    const handleExport = () => {
        exportData(current, prefix);
    }

    return <Page>
            <h2>{current.name}</h2>
            
            <CompanionWeaponBlockEditor weaponBlock={current} isBase={props.isbase} setWeaponBlock={setCurrent} />

            <Stack direction={"row"} spacing={1} paddingTop={1}>
                
                <ButtonFlat onClick={handleSave}>Save</ButtonFlat>
                <ButtonFlat onClick={handleBack}>Back to Library</ButtonFlat>
                <ButtonFlat onClick={handleExport}>Export</ButtonFlat>
            </Stack>
        </Page>
}